// src/components/OrganizationsTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Pagination,
  Dropdown,
  Tabs,
  Tab
} from 'react-bootstrap';
import { organizationsAPI } from '../services/api';

const OrganizationsTab = () => {
  // Состояния
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Фильтры и пагинация
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    include_deleted: false,
    only_active: true
  });
  
  // Данные форм
  const [organizationForm, setOrganizationForm] = useState({
    name: '',
    official_website: '',
    actual_address: '',
    legal_address: '',
    phone: '',
    email: '',
    description: ''
  });
  
  const [deleteReason, setDeleteReason] = useState('');
  const [restoreReason, setRestoreReason] = useState('');
  
  // Выбранная организация
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationStats, setOrganizationStats] = useState(null);
  
  // Загрузка организаций
  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        skip: (pagination.page - 1) * pagination.limit,
        limit: pagination.limit,
        include_deleted: filters.include_deleted,
        only_active: activeTab === 'active' ? true : 
                     activeTab === 'inactive' ? false : 
                     filters.only_active,
        search: filters.search || undefined
      };
      
      const response = await organizationsAPI.getOrganizations(params);
      
      if (response && response.items) {
        setOrganizations(response.items);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          pages: Math.ceil((response.total || 0) / pagination.limit)
        }));
      } else {
        setOrganizations([]);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError('Ошибка при загрузке организаций');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Загрузка статистики
  const loadStats = async () => {
    try {
      const response = await organizationsAPI.getStats();
      setOrganizationStats(response);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };
  
  // Загрузка при монтировании
  useEffect(() => {
    loadOrganizations();
    loadStats();
  }, [pagination.page, activeTab, filters.include_deleted]);
  
  // Обработчики изменений формы
  const handleFormChange = (field, value) => {
    setOrganizationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Создание организации
const handleCreateOrganization = async () => {
  try {
    setError('');
    
    // Валидация обязательных полей
    if (!organizationForm.name || !organizationForm.actual_address) {
      setError('Название и фактический адрес обязательны для заполнения');
      return;
    }
    
    // Подготовка данных для отправки
    const payload = {
      name: organizationForm.name.trim(),
      actual_address: organizationForm.actual_address.trim(),
      // Для необязательных полей: пустая строка -> null
      legal_address: organizationForm.legal_address?.trim() || null,
      phone: organizationForm.phone?.trim() || null,
      email: organizationForm.email?.trim() || null,
      official_website: organizationForm.official_website?.trim() || null,
      description: organizationForm.description?.trim() || null
    };
    
    console.log('📤 Sending organization data:', payload);
    
    const response = await organizationsAPI.createOrganization(payload);
    
    console.log('✅ Response:', response);
    
    if (response) {
      // Сброс формы и закрытие модального окна
      setOrganizationForm({
        name: '',
        official_website: '',
        actual_address: '',
        legal_address: '',
        phone: '',
        email: '',
        description: ''
      });
      setShowCreateModal(false);
      
      // Обновление списка
      await loadOrganizations();
      await loadStats();
      
      // Показать сообщение об успехе
      alert('Организация успешно создана!');
    }
  } catch (err) {
    console.error('❌ Error creating organization:', err);
    
    // Подробная обработка ошибки 422
    if (err.response?.status === 422) {
      const errorDetails = err.response?.data?.detail;
      console.error('Validation errors:', errorDetails);
      
      if (Array.isArray(errorDetails)) {
        // Ошибки валидации Pydantic в формате массива
        const errorMessages = errorDetails.map(e => {
          const field = e.loc?.join('.') || 'field';
          return `${field}: ${e.msg}`;
        }).join('\n');
        setError(`Ошибки валидации:\n${errorMessages}`);
      } else if (typeof errorDetails === 'string') {
        setError(errorDetails);
      } else {
        setError(JSON.stringify(errorDetails));
      }
    } else {
      setError(err.response?.data?.detail || 'Ошибка при создании организации');
    }
  }
};
  
  // Обновление организации
const handleUpdateOrganization = async () => {
  try {
    setError('');
    
    if (!selectedOrganization) return;
    
    // Подготовка данных для обновления
    const updateData = {};
    
    // Добавляем только измененные поля
    Object.keys(organizationForm).forEach(key => {
      if (organizationForm[key] !== '') {
        updateData[key] = organizationForm[key].trim();
      }
    });
    
    // Конвертируем пустые строки в null
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === '') {
        updateData[key] = null;
      }
    });
    
    console.log('📤 Updating organization with data:', updateData);
    
    const response = await organizationsAPI.updateOrganization(
      selectedOrganization.id,
      updateData
    );
    
    console.log('✅ Update response:', response);
    
    if (response) {
      setShowEditModal(false);
      await loadOrganizations();
      alert('Организация успешно обновлена!');
    }
  } catch (err) {
    console.error('❌ Error updating organization:', err);
    
    if (err.response?.status === 422) {
      const errorDetails = err.response?.data?.detail;
      console.error('Validation errors:', errorDetails);
      
      if (Array.isArray(errorDetails)) {
        const errorMessages = errorDetails.map(e => {
          const field = e.loc?.join('.') || 'field';
          return `${field}: ${e.msg}`;
        }).join('\n');
        setError(`Ошибки валидации:\n${errorMessages}`);
      } else {
        setError(errorDetails || 'Ошибка валидации данных');
      }
    } else {
      setError(err.response?.data?.detail || 'Ошибка при обновлении организации');
    }
  }
};
  
  // Мягкое удаление организации
  const handleSoftDeleteOrganization = async () => {
    try {
      if (!selectedOrganization) return;
      
      await organizationsAPI.softDeleteOrganization(selectedOrganization.id, {
        delete_reason: deleteReason
      });
      
      setShowDeleteModal(false);
      setDeleteReason('');
      await loadOrganizations();
      await loadStats();
      alert('Организация перемещена в архив');
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.detail || 'Ошибка при удалении организации');
    }
  };
  
  // Восстановление организации
  const handleRestoreOrganization = async () => {
    try {
      if (!selectedOrganization) return;
      
      await organizationsAPI.restoreOrganization(selectedOrganization.id, {
        restore_reason: restoreReason
      });
      
      setSelectedOrganization(null);
      setRestoreReason('');
      await loadOrganizations();
      await loadStats();
      alert('Организация восстановлена');
    } catch (err) {
      console.error('Error restoring organization:', err);
      setError(err.response?.data?.detail || 'Ошибка при восстановлении организации');
    }
  };
  
  // Переключение статуса активности
  const handleToggleStatus = async (organizationId) => {
    try {
      await organizationsAPI.toggleOrganizationStatus(organizationId);
      await loadOrganizations();
      await loadStats();
      alert('Статус организации изменен');
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Ошибка при изменении статуса');
    }
  };
  
  // Открытие модального окна редактирования
  const openEditModal = (organization) => {
    setSelectedOrganization(organization);
    setOrganizationForm({
      name: organization.name || '',
      official_website: organization.official_website || '',
      actual_address: organization.actual_address || '',
      legal_address: organization.legal_address || '',
      phone: organization.phone || '',
      email: organization.email || '',
      description: organization.description || ''
    });
    setShowEditModal(true);
  };
  
  // Открытие модального окна удаления
  const openDeleteModal = (organization) => {
    setSelectedOrganization(organization);
    setDeleteReason('');
    setShowDeleteModal(true);
  };
  
  // Открытие модального окна деталей
  const openDetailsModal = (organization) => {
    setSelectedOrganization(organization);
    setShowDetailsModal(true);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Получение статуса организации
  const getOrganizationStatus = (organization) => {
    if (organization.is_deleted) {
      return { variant: 'danger', text: 'В архиве' };
    }
    if (!organization.is_active) {
      return { variant: 'warning', text: 'Неактивна' };
    }
    return { variant: 'success', text: 'Активна' };
  };
  
  // Обработка поиска
  const handleSearch = (e) => {
    e.preventDefault();
    loadOrganizations();
  };
  
  // Обработка изменения страницы
  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, page: pageNumber }));
  };
  
  // Обработка изменения фильтров
  const handleFilterChange = (filter, value) => {
    setFilters(prev => ({ ...prev, [filter]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Обработка изменения вкладки
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Рендер пагинации
  const renderPagination = () => {
    const items = [];
    
    for (let number = 1; number <= pagination.pages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === pagination.page}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev 
          disabled={pagination.page === 1}
          onClick={() => handlePageChange(pagination.page - 1)}
        />
        {items}
        <Pagination.Next 
          disabled={pagination.page === pagination.pages}
          onClick={() => handlePageChange(pagination.page + 1)}
        />
      </Pagination>
    );
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0">
            <i className="bi bi-building me-2"></i>
            Управление организациями
          </h1>
          <p className="text-muted mb-0">
            Создание, редактирование и управление медицинскими организациями
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button 
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="d-flex align-items-center gap-2"
          >
            <i className="bi bi-plus-lg"></i>
            Добавить организацию
          </Button>
        </Col>
      </Row>
      
      {/* Фильтры и поиск */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md={6}>
              <Form onSubmit={handleSearch}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Поиск по названию, адресу, телефону или email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                  <Button variant="primary" type="submit">
                    <i className="bi bi-search"></i>
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-3">
                <Form.Check
                  type="switch"
                  id="include-deleted"
                  label="Показывать архивные"
                  checked={filters.include_deleted}
                  onChange={(e) => handleFilterChange('include_deleted', e.target.checked)}
                />
                
                <Form.Check
                  type="switch"
                  id="only-active"
                  label="Только активные"
                  checked={filters.only_active}
                  onChange={(e) => handleFilterChange('only_active', e.target.checked)}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Вкладки */}
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabChange}
        className="mb-4"
      >
        <Tab eventKey="all" title="Все">
          <div className="mt-3">
            {renderOrganizationsTable()}
          </div>
        </Tab>
        <Tab eventKey="active" title="Активные">
          <div className="mt-3">
            {renderOrganizationsTable()}
          </div>
        </Tab>
        <Tab eventKey="inactive" title="Неактивные">
          <div className="mt-3">
            {renderOrganizationsTable()}
          </div>
        </Tab>
      </Tabs>
      
      {/* Пагинация */}
      {pagination.pages > 1 && renderPagination()}
      
      {/* Модальные окна */}
      <CreateOrganizationModal 
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        organizationForm={organizationForm}
        handleFormChange={handleFormChange}
        handleCreateOrganization={handleCreateOrganization}
        error={error}
      />
      
      <EditOrganizationModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        organizationForm={organizationForm}
        handleFormChange={handleFormChange}
        handleUpdateOrganization={handleUpdateOrganization}
        error={error}
        selectedOrganization={selectedOrganization}
      />
      
      <DeleteOrganizationModal 
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        deleteReason={deleteReason}
        setDeleteReason={setDeleteReason}
        handleSoftDeleteOrganization={handleSoftDeleteOrganization}
        selectedOrganization={selectedOrganization}
      />
      
      <OrganizationDetailsModal 
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        selectedOrganization={selectedOrganization}
        formatDate={formatDate}
        getOrganizationStatus={getOrganizationStatus}
        handleToggleStatus={handleToggleStatus}
        handleRestoreOrganization={handleRestoreOrganization}
        restoreReason={restoreReason}
        setRestoreReason={setRestoreReason}
      />
    </Container>
  );
  
  // Функция рендеринга таблицы организаций
  function renderOrganizationsTable() {
    if (loading) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Загрузка организаций...</p>
          </Card.Body>
        </Card>
      );
    }
    
    if (error) {
      return (
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <Button 
            variant="outline-danger" 
            size="sm" 
            className="ms-3"
            onClick={loadOrganizations}
          >
            Повторить
          </Button>
        </Alert>
      );
    }
    
    if (organizations.length === 0) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="bi bi-building display-1 text-muted opacity-50 mb-3"></i>
            <h5>Организации не найдены</h5>
            <p className="text-muted mb-0">
              {filters.search 
                ? 'Попробуйте изменить условия поиска' 
                : 'Создайте первую организацию'}
            </p>
            {!filters.search && (
              <Button 
                variant="primary" 
                className="mt-3"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Добавить организацию
              </Button>
            )}
          </Card.Body>
        </Card>
      );
    }
    
    return (
      <Card>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th width="50">ID</th>
                  <th>Название</th>
                  <th>Адрес</th>
                  <th>Контакты</th>
                  <th>Статус</th>
                  <th>Дата создания</th>
                  <th width="120">Действия</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map(organization => {
                  const status = getOrganizationStatus(organization);
                  return (
                    <tr key={organization.id}>
                      <td>
                        <code className="text-muted">#{organization.id}</code>
                      </td>
                      <td>
                        <div className="fw-semibold hover-primary cursor-pointer"
                             onClick={() => openDetailsModal(organization)}>
                          {organization.name}
                        </div>
                        {organization.description && (
                          <small className="text-muted d-block mt-1">
                            {organization.description.length > 50
                              ? `${organization.description.substring(0, 50)}...`
                              : organization.description}
                          </small>
                        )}
                      </td>
                      <td>
                        <div>{organization.actual_address}</div>
                        {organization.legal_address && (
                          <small className="text-muted">
                            Юр. адрес: {organization.legal_address}
                          </small>
                        )}
                      </td>
                      <td>
                        {organization.phone && (
                          <div className="mb-1">
                            <i className="bi bi-telephone me-1"></i>
                            {organization.phone}
                          </div>
                        )}
                        {organization.email && (
                          <div>
                            <i className="bi bi-envelope me-1"></i>
                            {organization.email}
                          </div>
                        )}
                        {organization.official_website && (
                          <div className="mt-1">
                            <i className="bi bi-globe me-1"></i>
                            <a 
                              href={organization.official_website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              Сайт
                            </a>
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge bg={status.variant}>
                          {status.text}
                        </Badge>
                        {organization.users_count !== null && (
                          <div className="text-muted small mt-1">
                            Пользователей: {organization.users_count}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="small">
                          {formatDate(organization.created_at)}
                        </div>
                        {organization.created_by_name && (
                          <div className="text-muted small">
                            Создал: {organization.created_by_name}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Просмотреть"
                            onClick={() => openDetailsModal(organization)}
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            title="Редактировать"
                            onClick={() => openEditModal(organization)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Dropdown>
                            <Dropdown.Toggle 
                              variant="outline-secondary" 
                              size="sm" 
                              id={`dropdown-${organization.id}`}
                            />
                            <Dropdown.Menu>
                              {!organization.is_deleted && (
                                <>
                                  <Dropdown.Item
                                    onClick={() => handleToggleStatus(organization.id)}
                                  >
                                    <i className="bi bi-power me-2"></i>
                                    {organization.is_active ? 'Деактивировать' : 'Активировать'}
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => openDeleteModal(organization)}
                                  >
                                    <i className="bi bi-trash me-2"></i>
                                    В архив
                                  </Dropdown.Item>
                                </>
                              )}
                              {organization.is_deleted && (
                                <Dropdown.Item
                                  className="text-success"
                                  onClick={() => {
                                    setSelectedOrganization(organization);
                                    setRestoreReason('');
                                  }}
                                >
                                  <i className="bi bi-arrow-clockwise me-2"></i>
                                  Восстановить
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  }
};

// Модальное окно создания организации
const CreateOrganizationModal = ({ 
  show, 
  onHide, 
  organizationForm, 
  handleFormChange, 
  handleCreateOrganization,
  error 
}) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>
        <i className="bi bi-building me-2"></i>
        Создание организации
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Название организации <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название медицинской организации"
                value={organizationForm.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Официальное наименование медицинской организации
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Фактический адрес <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите фактический адрес"
                value={organizationForm.actual_address}
                onChange={(e) => handleFormChange('actual_address', e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Юридический адрес</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите юридический адрес"
                value={organizationForm.legal_address}
                onChange={(e) => handleFormChange('legal_address', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Телефон</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+7 (XXX) XXX-XX-XX"
                value={organizationForm.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
                <Form.Control
                type="url"
                placeholder="https://example.com"
                value={organizationForm.official_website}
                onChange={(e) => handleFormChange('official_website', e.target.value)}
                isInvalid={organizationForm.official_website && !organizationForm.official_website.startsWith('http')}
                />
                <Form.Control.Feedback type="invalid">
                Сайт должен начинаться с http:// или https://
                </Form.Control.Feedback>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Официальный сайт</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com"
                value={organizationForm.official_website}
                onChange={(e) => handleFormChange('official_website', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Дополнительная информация об организации"
                value={organizationForm.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button 
        variant="primary" 
        onClick={handleCreateOrganization}
        disabled={!organizationForm.name || !organizationForm.actual_address}
      >
        <i className="bi bi-check-lg me-2"></i>
        Создать организацию
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно редактирования организации
const EditOrganizationModal = ({ 
  show, 
  onHide, 
  organizationForm, 
  handleFormChange, 
  handleUpdateOrganization,
  error,
  selectedOrganization 
}) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>
        <i className="bi bi-pencil-square me-2"></i>
        Редактирование организации
        {selectedOrganization && (
          <span className="text-muted ms-2">ID: #{selectedOrganization.id}</span>
        )}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Название организации</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название"
                value={organizationForm.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Фактический адрес</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите фактический адрес"
                value={organizationForm.actual_address}
                onChange={(e) => handleFormChange('actual_address', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Юридический адрес</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите юридический адрес"
                value={organizationForm.legal_address}
                onChange={(e) => handleFormChange('legal_address', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Телефон</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+7 (XXX) XXX-XX-XX"
                value={organizationForm.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="example@domain.ru"
                value={organizationForm.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Официальный сайт</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com"
                value={organizationForm.official_website}
                onChange={(e) => handleFormChange('official_website', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Дополнительная информация"
                value={organizationForm.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button 
        variant="primary" 
        onClick={handleUpdateOrganization}
      >
        <i className="bi bi-check-lg me-2"></i>
        Сохранить изменения
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно удаления организации
const DeleteOrganizationModal = ({ 
  show, 
  onHide, 
  deleteReason, 
  setDeleteReason, 
  handleSoftDeleteOrganization,
  selectedOrganization 
}) => (
  <Modal show={show} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title className="text-danger">
        <i className="bi bi-trash me-2"></i>
        Переместить в архив
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Alert variant="warning" className="mb-3">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Организация будет перемещена в архив. Вы сможете восстановить её позже.
      </Alert>
      
      {selectedOrganization && (
        <div className="mb-3">
          <p>
            Вы уверены, что хотите переместить в архив организацию 
            <strong> "{selectedOrganization.name}"</strong>?
          </p>
        </div>
      )}
      
      <Form.Group className="mb-3">
        <Form.Label>Причина архивации (необязательно)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          placeholder="Укажите причину архивации..."
          value={deleteReason}
          onChange={(e) => setDeleteReason(e.target.value)}
        />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button variant="danger" onClick={handleSoftDeleteOrganization}>
        <i className="bi bi-trash me-2"></i>
        Переместить в архив
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно деталей организации
const OrganizationDetailsModal = ({
  show,
  onHide,
  selectedOrganization,
  formatDate,
  getOrganizationStatus,
  handleToggleStatus,
  handleRestoreOrganization,
  restoreReason,
  setRestoreReason
}) => {
  if (!selectedOrganization) return null;
  
  const status = getOrganizationStatus(selectedOrganization);
  
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-info-circle me-2"></i>
          Информация об организации
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col>
            <h4 className="mb-1">{selectedOrganization.name}</h4>
            <Badge bg={status.variant} className="me-2">
              {status.text}
            </Badge>
            <code className="text-muted">ID: #{selectedOrganization.id}</code>
          </Col>
          <Col xs="auto">
            <div className="d-flex gap-2">
              {!selectedOrganization.is_deleted && (
                <Button
                  variant={selectedOrganization.is_active ? "warning" : "success"}
                  size="sm"
                  onClick={() => handleToggleStatus(selectedOrganization.id)}
                >
                  <i className={`bi bi-power me-1`}></i>
                  {selectedOrganization.is_active ? 'Деактивировать' : 'Активировать'}
                </Button>
              )}
            </div>
          </Col>
        </Row>
        
        <Row className="g-3">
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-geo-alt me-2"></i>
                  Адреса
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Фактический адрес:</strong>
                  <div>{selectedOrganization.actual_address}</div>
                </div>
                {selectedOrganization.legal_address && (
                  <div>
                    <strong>Юридический адрес:</strong>
                    <div>{selectedOrganization.legal_address}</div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-telephone me-2"></i>
                  Контакты
                </h6>
              </Card.Header>
              <Card.Body>
                {selectedOrganization.phone && (
                  <div className="mb-2">
                    <strong>Телефон:</strong>
                    <div>
                      <i className="bi bi-telephone me-1"></i>
                      {selectedOrganization.phone}
                    </div>
                  </div>
                )}
                {selectedOrganization.email && (
                  <div className="mb-2">
                    <strong>Email:</strong>
                    <div>
                      <i className="bi bi-envelope me-1"></i>
                      {selectedOrganization.email}
                    </div>
                  </div>
                )}
                {selectedOrganization.official_website && (
                  <div>
                    <strong>Сайт:</strong>
                    <div>
                      <i className="bi bi-globe me-1"></i>
                      <a 
                        href={selectedOrganization.official_website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        {selectedOrganization.official_website}
                      </a>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          {selectedOrganization.description && (
            <Col md={12}>
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">
                    <i className="bi bi-card-text me-2"></i>
                    Описание
                  </h6>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{selectedOrganization.description}</p>
                </Card.Body>
              </Card>
            </Col>
          )}
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-clock-history me-2"></i>
                  История
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Создана:</strong>
                  <div>{formatDate(selectedOrganization.created_at)}</div>
                  {selectedOrganization.created_by_name && (
                    <div className="text-muted small">
                      пользователем {selectedOrganization.created_by_name}
                    </div>
                  )}
                </div>
                {selectedOrganization.updated_at && (
                  <div className="mb-2">
                    <strong>Обновлена:</strong>
                    <div>{formatDate(selectedOrganization.updated_at)}</div>
                    {selectedOrganization.updated_by_name && (
                      <div className="text-muted small">
                        пользователем {selectedOrganization.updated_by_name}
                      </div>
                    )}
                  </div>
                )}
                {selectedOrganization.deleted_at && (
                  <div>
                    <strong>В архиве с:</strong>
                    <div>{formatDate(selectedOrganization.deleted_at)}</div>
                    {selectedOrganization.deleted_by_name && (
                      <div className="text-muted small">
                        пользователем {selectedOrganization.deleted_by_name}
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-people me-2"></i>
                  Пользователи
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="text-center py-4">
                  <i className="bi bi-people display-1 text-muted opacity-50 mb-3"></i>
                  <h6>Пользователи организации</h6>
                  <p className="text-muted small mb-0">
                    Всего пользователей: {selectedOrganization.users_count || 0}
                  </p>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => {
                      // Здесь можно добавить переход к управлению пользователями организации
                      alert('Функционал управления пользователями организации будет добавлен позже');
                    }}
                  >
                    <i className="bi bi-eye me-1"></i>
                    Просмотреть пользователей
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Форма восстановления для архивных организаций */}
        {selectedOrganization.is_deleted && (
          <Card className="border-success">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">
                <i className="bi bi-arrow-clockwise me-2"></i>
                Восстановление организации
              </h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Причина восстановления (необязательно)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Укажите причину восстановления..."
                  value={restoreReason}
                  onChange={(e) => setRestoreReason(e.target.value)}
                />
              </Form.Group>
              <Button 
                variant="success" 
                className="w-100"
                onClick={handleRestoreOrganization}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Восстановить организацию
              </Button>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrganizationsTab;